
import React from "react";
import LoadingMessage from "../messages/LoadingMessage";

interface ConversationLoadingProps {
  isRegenerating: boolean;
  isAIGenerating: boolean;
  isPasting: boolean;
  isProcessingAI: boolean;
  regeneratingMessage?: string;
  aiGeneratingMessage?: string;
  pastingMessage?: string;
}

const ConversationLoading = ({ 
  isRegenerating, 
  isAIGenerating, 
  isPasting,
  isProcessingAI,
  regeneratingMessage = "Regenerating response...",
  aiGeneratingMessage = "Generating initial response...",
  pastingMessage = "Processing seller response..."
}: ConversationLoadingProps) => {
  // Determine if we're in a seller response flow (pasting then AI processing)
  const isSellerResponseFlow = isPasting || isProcessingAI;
  
  // Calculate which step we're on in the seller response flow
  let currentStep = 0;
  let totalSteps = 2; // For seller response flow: 1. Process seller message, 2. Generate AI response
  let progressMessage = "";
  let progressDetails = "";
  
  if (isSellerResponseFlow) {
    if (isPasting) {
      currentStep = 1;
      progressMessage = "Analyzing seller's message";
      progressDetails = "Extracting key information and determining response strategy";
    } else if (isProcessingAI) {
      currentStep = 2;
      progressMessage = "Crafting appropriate response";
      progressDetails = "Generating tailored reply based on conversation history";
    }
  }
  
  if (isRegenerating) {
    return (
      <div className="message-loading animate-fade-in">
        <LoadingMessage showProgress={true} progress={65}>
          <div className="space-y-1">
            <span className="text-sm font-medium">{regeneratingMessage}</span>
            <p className="text-xs text-muted-foreground">
              Improving response quality and addressing your feedback
            </p>
          </div>
        </LoadingMessage>
      </div>
    );
  }
  
  if (isAIGenerating && !isSellerResponseFlow) {
    return (
      <div className="message-loading animate-fade-in">
        <LoadingMessage showProgress={true} progress={75}>
          <div className="space-y-1">
            <span className="text-sm font-medium">{aiGeneratingMessage}</span>
            <p className="text-xs text-muted-foreground">
              Creating a professional refund request tailored to your situation
            </p>
          </div>
        </LoadingMessage>
      </div>
    );
  }
  
  if (isSellerResponseFlow) {
    return (
      <div className="message-loading animate-fade-in">
        <LoadingMessage 
          showProgress={true}
          progressSteps={totalSteps}
          currentStep={currentStep}
        >
          <div className="space-y-1">
            <span className="text-sm font-medium">{progressMessage}</span>
            <p className="text-xs text-muted-foreground">{progressDetails}</p>
          </div>
        </LoadingMessage>
      </div>
    );
  }
  
  return null;
};

export default ConversationLoading;
